import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AuthenticationService } from '@services/authentication.service';
import { inject } from '@angular/core';
import { AuthorizationService } from '@services/authorization.service';
import { ComponentService } from '@services/component.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { UserRole } from '../types/User';
import { Action, Entity } from '../types/Authorization';
import { MainFeaturesRoutesEnum } from '../types/vev-route';
import { MainFeaturesRoutes } from '../routes/main-features.routes';

const LOGIN_ROUTE = '/auth/login';

export const authenticationGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authenticationService = inject(AuthenticationService);
  const router = inject(Router);
  const authorizationService = inject(AuthorizationService);
  const componentService = inject(ComponentService);
  // If user is not authenticated redirect to login page
  if (!authenticationService.isAuthenticated()) {
    const queryParams = { returnUrl: state.url };
    await router.navigate([LOGIN_ROUTE], { queryParams });
    return false;
  }

  // If user is super admin or super role and route is shown in super tenant
  if (route.data?.displayInSuperTenant && (authorizationService.isSuperAdmin() || authorizationService.isSuperRole())) {
    return true;
  }

  const featureToggle = route?.data?.featureToggle;
  if (featureToggle && !componentService.isFeatureActive(featureToggle)) {
    const redirectRoute = getDefaultRoute(authorizationService.getLoggedUser().role);
    await router.navigate([redirectRoute]);
    if (redirectRoute === LOGIN_ROUTE) {
      authenticationService.clearLoginInformation();
    }
    return false;
  }

  const auth = route?.data?.auth;
  const component = route?.data?.component;

  // Route is not restricted by any permissions or components
  if (!auth && !component) {
    return true;
  }

  const redirectRoute = getDefaultRoute(authorizationService.getLoggedUser().role);
  if (component && !componentService.isActive(component)) {
    const isAllowedInSuperTenantWhenInactive = route.data?.isAllowedInSuperTenantWhenInactive;
    if (
      (authorizationService.isSuperAdmin() || authorizationService.isSuperRole()) &&
      isAllowedInSuperTenantWhenInactive
    ) {
      return true;
    }

    await router.navigate([redirectRoute]);
    return false;
  }

  const navigationService = inject(NavigationService);
  const isAllowed = navigationService.isRouteAllowed(auth);
  const isRoleAllowed = navigationService.isRoleAllowed(route.data?.authorizedRoles);

  if (isAllowed && isRoleAllowed) {
    return true;
  }
  await router.navigate([redirectRoute]);

  if (redirectRoute === LOGIN_ROUTE) {
    authenticationService.clearLoginInformation();
  }
  return false;
};

const getDefaultRoute = (userRole: string): string => {
  const authorizationService = inject(AuthorizationService);
  switch (userRole) {
    case UserRole.SUPER_ADMIN:
      if (authorizationService.canAccess(Entity.TENANT, Action.LIST)) {
        return MainFeaturesRoutesEnum.TENANTS;
      }
      break;
    case UserRole.ADMIN:
    case UserRole.BASIC:
    case UserRole.DEMO:
    default:
      if (authorizationService.isSuperRole() && authorizationService.canAccess(Entity.TENANT, Action.LIST)) {
        return MainFeaturesRoutesEnum.TENANTS;
      }
      return getFirstAuthorizedRoute();
  }

  return LOGIN_ROUTE;
};

const getFirstAuthorizedRoute = (): string => {
  const navigationService = inject(NavigationService);
  const componentService = inject(ComponentService);
  const foundRoute = MainFeaturesRoutes.filter((route) => route.path !== '').find(
    (route) =>
      (!route.data?.featureToggle || componentService.isFeatureActive(route.data.featureToggle)) &&
      navigationService.isRouteAllowed(route.data?.auth),
  );

  if (foundRoute) {
    return foundRoute.path;
  }

  return LOGIN_ROUTE;
};
